/*
 * Copyright (c) Nexign, JSC, 2019-2020.
 */
/**
 * Container component for Basket item
 *
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @version 1.3.0
 * @since 1.0.0
 */
import { PureComponent, ChangeEvent, createRef, RefObject } from 'react';

import debounce from 'lodash/debounce';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import GTMOperations from '../../../../../../modules/analytics/GoogleTagManager/GTMOperations';
import { EGTMMBasketInteractionType } from '../../../../../../modules/analytics/GoogleTagManager/GTMOperations/GTMBasketOperations';
import { IGTMPushAnalyticPayload } from '../../../../../../modules/analytics/GoogleTagManager/types/GTMActionTypes';
import IBasketTranslation from '../../../../../../modules/internationalization/types/common/basket';
import { IRootReducer } from '../../../../../../reducers/rootReducer';
import { Checkbox, TextBlock } from '../../../../../common';
import { makeSelectorTableBasketCount } from '../../../../selectors/tableSelectors';
import { basketGetListStart, basketGetListSuccessful } from '../basketActions';
import { makeSelectorBasketList } from '../basketSelectors';
import { IBasketGTMProps, IBasketItem } from '../basketTypes';
import { BasketItemContainerStyled, BasketItemStyled, BasketSearchNotFound } from './styled/BasketContainerStyled';


interface IConnectedProps {
    count: number | null;
    list: IBasketItem[];
}

interface IDispatchProps {
    getBasketListStart: () => void;
    updateBasketItems: (items: IBasketItem[]) => void;
}

interface IBasketItemContainerProps {
    gtmProps?: IBasketGTMProps;
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => void;
    translation: IBasketTranslation;
}

export type TBasketItemContainerProps = IBasketItemContainerProps & IConnectedProps & IDispatchProps;

export class BasketItemContainer extends PureComponent<TBasketItemContainerProps> {
    private itemsHeight: number = this.props.list.length * 44;
    private basketContainerRef: RefObject<HTMLDivElement> = createRef<HTMLDivElement>();
    private count = this.props.count === null ? 0 : this.props.count;

    componentDidMount(): void {
        this.basketContainerRef.current &&
        this.basketContainerRef.current.addEventListener('scroll', this.onChangeScrollDebounce as any);
    }

    componentWillUnmount(): void {
        this.basketContainerRef.current &&
        this.basketContainerRef.current.removeEventListener('scroll', this.onChangeScrollDebounce as any);
    }

    onChangeScroll = (event: ChangeEvent<HTMLDivElement>) => {
        const el = event.target;
        const scrollTop = el.scrollTop;
        const scrollHeight = el.scrollHeight;
        const clientHeight = el.clientHeight;
        const pos = (clientHeight + scrollTop) * 100 / scrollHeight;
        if (pos > 85 && this.props.list.length < this.count) {
            this.props.getBasketListStart();
        }
    };
    private onChangeScrollDebounce = debounce(this.onChangeScroll, 250);

    onCheck = (id: number, value: boolean) => {
        const items: IBasketItem[] = this.props.list.map(item => {
            if (item.id === id) {
                return {
                    ...item,
                    checked: value,
                };
            }
            return item;
        });
        this.props.updateBasketItems(items);
        if (this.props.gtmProps) this.props.pushGtmAnalytic({
            operation: GTMOperations.basket.onBasketPopupSelectOneClick(
                this.props.gtmProps.sectionType,
                value ? EGTMMBasketInteractionType.ON : EGTMMBasketInteractionType.OFF,
                this.props.gtmProps.valueType),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
    };

    render() {
        return <BasketItemContainerStyled height={this.itemsHeight > 440 ? 440 : this.itemsHeight}
                                        ref={this.basketContainerRef}>
            {
                this.props.list.length > 0
                    ? this.props.list.map((item: IBasketItem, index: number) => (
                        <BasketItemStyled
                            data-basket-item={`basket-item-${item.id}-${index}`}
                            key={`${item.id}-${index}`}
                        >
                            <Checkbox
                                checked={item.checked}
                                className="basket__item-checkbox"
                                onChange={(value: boolean) => this.onCheck(item.id, value)}
                            />
                            <TextBlock
                                className={`basket__item-label basket__item-label-${item.id}`}
                            >
                                {item.label}
                            </TextBlock>
                        </BasketItemStyled>
                    ))
                    : <BasketSearchNotFound>
                        <TextBlock>
                            {this.props.translation.noSearchMatcher}
                        </TextBlock>
                    </BasketSearchNotFound>
            }
        </BasketItemContainerStyled>
    };
}
const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    count: makeSelectorTableBasketCount(),
    list: makeSelectorBasketList(),
});

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    getBasketListStart: () => dispatch(basketGetListStart()),
    updateBasketItems: (items: IBasketItem[]) => dispatch(basketGetListSuccessful({ list: items })),
});

export default connect(mapStateToProps, mapDispatchToProps)(BasketItemContainer as any) as any;
